import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { LEAD_STATUS, resolveClientType, fmtDate } from "@/lib/domain";
import { Chip } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Mail, Phone, MapPin, Users, HardHat, FileText, Trash2, TriangleAlert } from "lucide-react";
import { toast, Toaster } from "sonner";

const CLIENT_TYPE_LABEL = { privat: "Privat", firma: "Firma" };

const empty = {
  name: "", company: "", email: "", phone: "", address: "", postal_code: "", city: "",
  lead_status: "Warm", source: "", notes: "", client_type: "privat", website: "",
  contact_persons: [],
};

export default function Clients() {
  const nav = useNavigate();
  const [clients, setClients] = useState([]);
  const [objects, setObjects] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [duplicates, setDuplicates] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  const load = () => {
    Promise.all([api.get("/clients"), api.get("/objects"), api.get("/quotes")])
      .then(([c, o, q]) => { setClients(c.data); setObjects(o.data); setQuotes(q.data); });
  };
  useEffect(() => { load(); }, []);

  // Real per-client aggregates, computed once from the three already-fetched
  // collections (no per-card requests).
  const byClient = useMemo(() => {
    const map = {};
    for (const c of clients) map[c.id] = { objects: [], quotes: [] };
    for (const o of objects) map[o.client_id]?.objects.push(o);
    for (const q of quotes) map[q.client_id]?.quotes.push(q);
    return map;
  }, [clients, objects, quotes]);

  const lastActivity = (c) => {
    const agg = byClient[c.id];
    if (!agg) return null;
    const dates = [...agg.objects.map((o) => o.created_at), ...agg.quotes.map((q) => q.created_at)].filter(Boolean);
    if (!dates.length) return null;
    return dates.reduce((a, b) => (a > b ? a : b));
  };

  // Single definition of "active client" reused by both the Aktiv filter and
  // the stat column: has at least one object that isn't finished/cancelled.
  const hasActiveObject = (c) => (byClient[c.id]?.objects || []).some((o) => !["Abgeschlossen", "Storniert"].includes(o.status));

  const stats = useMemo(() => ({
    total: clients.length,
    firma: clients.filter((c) => resolveClientType(c) === "firma").length,
    privat: clients.filter((c) => resolveClientType(c) === "privat").length,
    withActiveObjects: clients.filter(hasActiveObject).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [clients, byClient]);

  const filtered = clients.filter((c) => {
    if (filter === "privat" && resolveClientType(c) !== "privat") return false;
    if (filter === "firma" && resolveClientType(c) !== "firma") return false;
    if (filter === "aktiv" && !hasActiveObject(c)) return false;
    if (search) {
      const n = search.toLowerCase();
      const hay = [c.name, c.company, c.email, c.phone, c.website, c.city].map((x) => (x || "").toLowerCase()).join(" ");
      if (!hay.includes(n)) return false;
    }
    return true;
  });

  const startNew = () => { setEditing(null); setForm(empty); setDuplicates(null); setOpen(true); };
  const startEdit = (c) => { setEditing(c); setForm({ ...empty, ...c, client_type: resolveClientType(c), contact_persons: c.contact_persons || [] }); setDuplicates(null); setOpen(true); };

  const save = async (force = false) => {
    try {
      if (editing) {
        const payload = { ...form }; delete payload.id;
        await api.patch(`/clients/${editing.id}`, payload);
        toast.success("Клиент обновлён");
      } else {
        await api.post("/clients", { ...form, force });
        toast.success("Клиент добавлен");
      }
      setOpen(false); setDuplicates(null);
      load();
    } catch (e) {
      if (e.response?.status === 409 && e.response.data?.detail?.candidates) {
        setDuplicates(e.response.data.detail.candidates);
        return;
      }
      toast.error("Ошибка: " + (e.response?.data?.detail || e.message));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Удалить клиента?")) return;
    await api.delete(`/clients/${id}`);
    load();
  };

  const addContact = () => setForm({ ...form, contact_persons: [...form.contact_persons, { name: "", position: "", phone: "", email: "" }] });
  const editContact = (i, k, v) => {
    const cp = [...form.contact_persons];
    cp[i] = { ...cp[i], [k]: v };
    setForm({ ...form, contact_persons: cp });
  };
  const rmContact = (i) => setForm({ ...form, contact_persons: form.contact_persons.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-6">
      <Toaster theme="dark" position="top-right" />
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl">Клиенты</h1>
          <p className="text-slate-400 mt-1 text-sm">{clients.length} клиентов в базе</p>
        </div>
        <Button data-testid="new-client-button" onClick={startNew} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">
          <Plus size={16} /> Добавить клиента
        </Button>
      </div>

      {/* Stats — compact cards on mobile, right column on desktop (grid handles both) */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-5">
        <div className="space-y-4 min-w-0">
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              data-testid="clients-search-input"
              placeholder="Поиск: имя, компания, email, телефон, сайт, город…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-[#131924] border-[#212b3d] h-10 flex-1"
            />
            <div className="flex gap-2 overflow-x-auto pb-1">
              {[["all", "Все"], ["privat", "Privat"], ["firma", "Firma"], ["aktiv", "Aktiv"]].map(([v, l]) => (
                <button key={v} onClick={() => setFilter(v)} data-testid={`client-filter-${v}`}
                  className={`whitespace-nowrap px-3 py-1.5 rounded-md text-xs font-medium border transition ${
                    filter === v ? "bg-amber-500/15 text-amber-300 border-amber-500/40" : "bg-[#131924] text-slate-400 border-[#212b3d] hover:bg-[#1c2434]"
                  }`}>{l}</button>
              ))}
            </div>
          </div>

          {filtered.length === 0 && (
            <Card className="p-10 bg-[#131924] border-[#212b3d] text-center text-slate-500">
              <Users className="mx-auto mb-3 opacity-50" size={32} />
              {clients.length === 0 ? "Клиентов ещё нет." : "Ничего не найдено."}
            </Card>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filtered.map((c) => {
              const lead = LEAD_STATUS.find((x) => x.value === c.lead_status);
              const agg = byClient[c.id] || { objects: [], quotes: [] };
              const primaryContact = (c.contact_persons || [])[0]?.name;
              const activity = lastActivity(c);
              return (
                <Card key={c.id} data-testid={`client-card-${c.id}`}
                  onClick={() => nav(`/clients/${c.id}`)}
                  className="p-5 bg-[#131924] border-[#212b3d] hover:border-amber-500/30 transition cursor-pointer">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-semibold text-base text-slate-100 truncate">{c.company || c.name}</div>
                      {(primaryContact || (c.company && c.name)) && (
                        <div className="text-xs text-slate-500 truncate">{primaryContact || c.name}</div>
                      )}
                    </div>
                    <Chip className={lead?.cls}>{lead?.label}</Chip>
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <Chip className="bg-slate-500/15 text-slate-300 border-slate-500/30">
                      {CLIENT_TYPE_LABEL[resolveClientType(c)]}
                    </Chip>
                  </div>
                  <div className="space-y-1.5 text-xs text-slate-400 mb-4">
                    {c.email && <div className="flex items-center gap-2"><Mail size={12} /> <span className="truncate">{c.email}</span></div>}
                    {c.phone && <div className="flex items-center gap-2"><Phone size={12} /> {c.phone}</div>}
                    {c.city && <div className="flex items-center gap-2"><MapPin size={12} /> {c.postal_code} {c.city}</div>}
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-500 pt-3 border-t border-[#212b3d]">
                    <span className="flex items-center gap-1"><HardHat size={12} /> {agg.objects.length}</span>
                    <span className="flex items-center gap-1"><FileText size={12} /> {agg.quotes.length}</span>
                    {activity && <span className="ml-auto truncate">Активность: {fmtDate(activity)}</span>}
                  </div>
                  <div className="flex gap-2 pt-3 mt-3 border-t border-[#212b3d]" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="outline" onClick={() => startEdit(c)}
                      className="flex-1 bg-transparent border-[#212b3d] hover:bg-[#1c2434]">Редактировать</Button>
                    <Button size="sm" variant="outline" onClick={() => del(c.id)}
                      className="bg-transparent border-[#212b3d] hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/40">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Right stat column (desktop) / stacks on top on mobile via grid order */}
        <div className="grid grid-cols-2 lg:grid-cols-1 gap-3 order-first lg:order-last h-fit">
          <StatBox label="Всего клиентов" value={stats.total} />
          <StatBox label="Firmenkunden" value={stats.firma} />
          <StatBox label="Privatkunden" value={stats.privat} />
          <StatBox label="С активными объектами" value={stats.withActiveObjects} />
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#131924] border-[#212b3d] text-slate-100 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Редактировать" : "Новый клиент"}</DialogTitle></DialogHeader>

          {duplicates && duplicates.length > 0 && (
            <Card className="p-3 bg-amber-500/10 border-amber-500/30 text-amber-200 text-sm space-y-2">
              <div className="flex items-center gap-2 font-medium"><TriangleAlert size={14} /> Возможно, клиент уже существует</div>
              <div className="space-y-1">
                {duplicates.map((d) => (
                  <div key={d.id} className="flex items-center justify-between gap-2 text-xs bg-[#131924]/60 rounded px-2 py-1.5">
                    <span className="truncate">{d.company || d.name} {d.email ? `· ${d.email}` : ""} {d.phone ? `· ${d.phone}` : ""}</span>
                    <button className="text-amber-300 hover:text-amber-100 shrink-0" onClick={() => nav(`/clients/${d.id}`)}>Открыть →</button>
                  </div>
                ))}
              </div>
              <Button size="sm" variant="outline" onClick={() => save(true)}
                className="bg-transparent border-amber-500/40 text-amber-200 hover:bg-amber-500/20">
                Всё равно создать нового
              </Button>
            </Card>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Тип клиента</Label>
              <Select value={form.client_type} onValueChange={(v) => setForm({ ...form, client_type: v })}>
                <SelectTrigger className="bg-[#0f1521] border-[#212b3d]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#131924] border-[#212b3d] text-slate-100">
                  <SelectItem value="privat">Privat</SelectItem>
                  <SelectItem value="firma">Firma</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Статус</Label>
              <Select value={form.lead_status} onValueChange={(v) => setForm({ ...form, lead_status: v })}>
                <SelectTrigger className="bg-[#0f1521] border-[#212b3d]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#131924] border-[#212b3d] text-slate-100">
                  {LEAD_STATUS.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2"><Label>Имя *</Label><Input data-testid="client-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0f1521] border-[#212b3d]" /></div>
            {form.client_type === "firma" && (
              <div className="sm:col-span-2"><Label>Компания</Label><Input value={form.company || ""} onChange={(e) => setForm({ ...form, company: e.target.value })} className="bg-[#0f1521] border-[#212b3d]" /></div>
            )}
            <div><Label>Эл. почта</Label><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0f1521] border-[#212b3d]" /></div>
            <div><Label>Телефон</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#0f1521] border-[#212b3d]" /></div>
            {form.client_type === "firma" && (
              <div className="sm:col-span-2"><Label>Website</Label><Input value={form.website || ""} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://…" className="bg-[#0f1521] border-[#212b3d]" /></div>
            )}
            <div className="sm:col-span-2"><Label>Адрес</Label><Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-[#0f1521] border-[#212b3d]" /></div>
            <div><Label>Индекс</Label><Input value={form.postal_code || ""} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className="bg-[#0f1521] border-[#212b3d]" /></div>
            <div><Label>Город</Label><Input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-[#0f1521] border-[#212b3d]" /></div>
            <div><Label>Источник</Label><Input value={form.source || ""} onChange={(e) => setForm({ ...form, source: e.target.value })} className="bg-[#0f1521] border-[#212b3d]" /></div>
            <div className="sm:col-span-2"><Label>Внутренние заметки</Label><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#0f1521] border-[#212b3d]" /></div>

            {form.client_type === "firma" && (
              <div className="sm:col-span-2">
                <div className="flex items-center justify-between mb-1.5">
                  <Label>Контактные лица</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addContact}
                    className="h-7 bg-transparent border-[#212b3d] hover:bg-[#1c2434] text-xs"><Plus size={12} /> Добавить</Button>
                </div>
                <div className="space-y-2">
                  {form.contact_persons.map((cp, i) => (
                    <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-1.5 items-center">
                      <Input value={cp.name} onChange={(e) => editContact(i, "name", e.target.value)} placeholder="Имя" className="bg-[#0f1521] border-[#212b3d] h-8 text-xs" />
                      <Input value={cp.position || ""} onChange={(e) => editContact(i, "position", e.target.value)} placeholder="Должность" className="bg-[#0f1521] border-[#212b3d] h-8 text-xs" />
                      <button onClick={() => rmContact(i)} className="text-slate-500 hover:text-red-400 p-1"><Trash2 size={13} /></button>
                      <Input value={cp.phone || ""} onChange={(e) => editContact(i, "phone", e.target.value)} placeholder="Телефон" className="bg-[#0f1521] border-[#212b3d] h-8 text-xs" />
                      <Input value={cp.email || ""} onChange={(e) => editContact(i, "email", e.target.value)} placeholder="Email" className="bg-[#0f1521] border-[#212b3d] h-8 text-xs col-span-2" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button data-testid="save-client-button" onClick={() => save(false)} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="rounded-lg border border-[#212b3d] bg-[#131924] p-4">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <div className="font-display font-bold text-xl text-amber-400">{value}</div>
    </div>
  );
}
